import { createStore } from 'vuex'
import sessionStore from './session'

const HotjarPlugin = store => {
  let identifyTimeout = null
  let lastIdentifyData = null
  let hotjarReady = false

  // Check if Hotjar is ready
  const checkHotjarReady = () => {
    hotjarReady = !!(window.hj && typeof window.hj === 'function')
    return hotjarReady
  }

  // Debounced identify function
  const identifyUser = () => {
    // Clear any pending identify calls
    if (identifyTimeout) {
      clearTimeout(identifyTimeout)
    }

    identifyTimeout = setTimeout(() => {
      if (!checkHotjarReady()) {
        return
      }

      try {
        const state = store.state
        const userId = state.session?.userId || null
        const language = state.locale?.language || navigator.language

        // Create identifier to prevent duplicate calls
        const currentData = JSON.stringify({ userId, language })

        // Only identify if data has actually changed
        if (currentData === lastIdentifyData) {
          return
        }

        // Call Hotjar identify
        window.hj('identify', userId, {
          language,
        })

        lastIdentifyData = currentData
      } catch {}
    }, 300)
  }

  // Watch for relevant mutations only
  store.subscribe(mutation => {
    if (
      mutation.type === 'SET_LANGUAGE' ||
      mutation.type === 'session/SET_STUDIO_TOKEN' ||
      mutation.type === 'session/SET_USER_ID'
    ) {
      identifyUser()
    }
  })

  // Initial identification on load
  const init = () => {
    if (checkHotjarReady()) {
      identifyUser()
    } else {
      // Retry until Hotjar is loaded (max 10 seconds)
      let attempts = 0
      const maxAttempts = 50

      const retryInterval = setInterval(() => {
        attempts++

        if (checkHotjarReady()) {
          clearInterval(retryInterval)
          identifyUser()
        } else if (attempts >= maxAttempts) {
          clearInterval(retryInterval)
        }
      }, 200)
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init()
  } else {
    window.addEventListener('DOMContentLoaded', init)
  }

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && hotjarReady) {
      identifyUser()
    }
  })
}

export const plugins = [HotjarPlugin]

export const store = createStore({
  plugins,
  state() {
    return {
      isInitialized: false,
      locale: {
        language: 'en-US',
      },
      currency: null,
      currencyName: null,
      isCurrencyEditable: true,
    }
  },
  mutations: {
    SET_INITIALIZED(state) {
      state.isInitialized = true
    },
    SET_LANGUAGE(state, locale = 'en-US') {
      state.locale.language = locale
    },
    SET_CURRENCY(state, currency) {
      state.currency = currency
    },
    SET_CURRENCY_NAME(state, value) {
      state.currencyName = value
    },
    SET_IS_CURRENCY_EDITABLE(state, value = true) {
      state.isCurrencyEditable = value
    },
  },
  getters: {
    getLanguage(state) {
      return state.locale.language
    },
  },
  modules: {
    session: {
      namespaced: true,
      state: sessionStore.state,
      actions: sessionStore.actions,
      mutations: sessionStore.mutations,
      getters: sessionStore.getters,
    },
  },
})
